import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Path, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import KogModalSideComponent from "@/main/components/modal/ModalSide";
import Button from "@/main/components/shared/button/button";
import KOGFileUploader from "@/main/components/shared/fileUploader/fileUploader";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputAutoCompleteCheckControlled from "@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled";

import { fileImportApi } from "@/main/api";
import { getCompany, resetReducerCompany, useCompanyReducer } from "@/main/store/company";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getFileType, resetReducerFileType, useFileTypeReducer } from "@/main/store/fileType";
import { getSystemApplication, useSystemApplicationReducer } from "@/main/store/systemApplication";

import { KeyValue } from "@/main/models/common";
import { CompanyFilterRequest, TermsOfUseSaveRequest } from "@/main/models/request";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusUserGroupEnum } from "@/main/services/enum/StatusUserGroupEnum";

import { getOptionsStatus } from "@/main/services/data/data";
import { isNullUndefined } from "@/main/services/reducerUtils";
import { getDefaultCountryOptionDrop, getDefaultOptionDrop, getTranslateByKey } from "@/main/services/utils";

import useAsyncModalAlert from "@/main/components/shared/asyncModalAlert/asyncModalAlert";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { Result } from "@/main/models/common/resultResponse";
import { TermsOfUseDetailsResponse } from "@/main/models/response";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { getCulture, useCultureReducer } from "@/main/store/culture";
import { AxiosError } from "axios";
import {
	INITIAL_TERMS_OF_USE_REGISTER_FORM,
	ITermsOfUseRegisterForm,
	ITermsOfUseRegisterFormOutput,
	termsOfUseRegisterSchema,
} from "./modalRegisterTermsUtils";
import * as S from "./styles";

const ID_BASE_COMPONENTS = "id-modalRegisterTermsOfUse";

interface IModalRegisterTermsOfUse {
	isOpen: boolean;
	handleClose: (refresh?: boolean) => void;
	fileImportGuid?: string;
}

function ModalRegisterTermsOfUseContent({ isOpen, handleClose, fileImportGuid }: Readonly<IModalRegisterTermsOfUse>) {
	const isEdit = !isNullUndefined(fileImportGuid);
	const dispatch = useDispatch();

	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingTerm, setIsLoadingTerm] = useState(false);
	const [termData, setTermData] = useState<TermsOfUseDetailsResponse>();
	const [hasChangedFile, setHasChangedFile] = useState<boolean>(false);
	const [showAlert, ModalAlert] = useAsyncModalAlert();

	const {
		countriesUser: { data: countries, isLoading: isLoadingCountries },
	} = useCountryReducer();

	const {
		companyType: { data: companyTypes, isLoading: isLoadingCompanyTypes },
	} = useCompanyTypeReducer();

	const {
		company: { data: companies, isLoading: isLoadingCompanies },
	} = useCompanyReducer();

	const {
		systemApplication: { data: systemApplications, isLoading: isLoadingSystemApplications },
	} = useSystemApplicationReducer();

	const {
		fileType: { data: fileTypes, isLoading: isLoadingFileTypes },
	} = useFileTypeReducer();

	const {
		culture: { data: cultures, isLoading: isLoadingCultures },
	} = useCultureReducer();

	const statusOptions = getOptionsStatus();
	const defaultStatusOption = statusOptions.find((option) => Number(option.key) === StatusUserGroupEnum.Active) || null;

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countries);
	const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(companyTypes);

	const {
		control,
		setValue,
		handleSubmit,
		reset,
		getValues,
		watch,
		formState: { errors },
	} = useForm<ITermsOfUseRegisterForm, any, ITermsOfUseRegisterFormOutput>({
		mode: "onChange",
		defaultValues: INITIAL_TERMS_OF_USE_REGISTER_FORM,
		resolver: zodResolver(termsOfUseRegisterSchema),
	});

	const watchedFile = watch("file");
	const filesForUploader = watchedFile ? [watchedFile] : [];

	const isDisabledFileTypeDrop = !fileTypes;

	const isDisabledCompanyTypeDrop = !companyTypes || companyTypes.length === 1 || isEdit;

	const isDisabledCompanyDrop = () => getValues().companyType === null || !companies || companies.length === 0;

	useEffect(() => {
		if (!isOpen) {
			reset(INITIAL_TERMS_OF_USE_REGISTER_FORM);
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !isEdit || !fileImportGuid) return;
		load(fileImportGuid);
	}, [isOpen, isEdit, fileImportGuid]);

	useEffect(() => {
		dispatch(resetReducerFileType());
		if (isNullUndefined(countries)) dispatch(getCountriesUser());
		if (isNullUndefined(companyTypes)) dispatch(getCompanyType({ screenCompanytype: true }));
		if (isNullUndefined(systemApplications)) dispatch(getSystemApplication());
	}, []);

	useEffect(() => {
		if (isEdit || !isOpen || !optionDefaultCountryDrop) return;

		callCultureApiDomain(optionDefaultCountryDrop);
		setValue("country", optionDefaultCountryDrop);

		if (optionDefaultCompanyTypeDrop && termData?.companyGuids.length === 0) {
			callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
		}
	}, [optionDefaultCountryDrop?.key, optionDefaultCompanyTypeDrop?.key, isOpen, isEdit]);

	useEffect(() => {
		if (isNullUndefined(termData)) return;

		if (fileTypes) {
			findAndSetValue("fileType", fileTypes, String(termData.idFileType));
		}

		if (companies && termData?.companyGuids.length !== 0) {
			setValue(
				"company",
				companies.filter((c) => termData?.companyGuids.includes(String(c.key))),
			);
		}
	}, [companies, fileTypes, JSON.stringify(termData)]);

	function callFileTypeApiDomain(systemApplicationSelected: KeyValue) {
		dispatch(getFileType(Number(systemApplicationSelected.key)));
	}

	function callCultureApiDomain(countrySelected: KeyValue) {
		dispatch(getCulture({ guidCountry: String(countrySelected.key) }));
	}

	function callCompanyApiDomain(countrySelected: KeyValue | null, companyTypeSelected: KeyValue | null) {
		dispatch(resetReducerCompany());

		if (countrySelected !== null) {
			const request: CompanyFilterRequest = {
				guidCountry: String(countrySelected.key),
				type: HeadQuarterBranchFilterEnum.All,
			};
			if (companyTypeSelected) {
				request.idCompanyType = String(companyTypeSelected.key);
				dispatch(getCompany(request));
			}
		}
	}

	function onSubmitForm(formData: ITermsOfUseRegisterFormOutput) {
		if (isNullUndefined(formData.file)) {
			toast.error(getTranslateByKey("importInventory.pleaseSelectAtLeast1File"));
			return;
		}
		const request: TermsOfUseSaveRequest = {
			fileImportGuid: fileImportGuid ?? "",
			cultureGuid: formData.culture?.key ? String(formData.culture.key) : "",
			idSystemApplication: Number(formData.systemApplication.key),
			idCompanyType: Number(formData.companyType.key),
			companyGuids: formData.company.map((c) => String(c.key)),
			idFileType: formData.fileType?.key ? Number(formData.fileType.key) : 0,
			active: Number(formData.statusRegister.key) === StatusUserGroupEnum.Active,
		};

		setIsSaving(true);
		fileImportApi
			.saveTerm(request, formData.file, !isEdit || (isEdit && hasChangedFile))
			.then(() => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				handleClose(true);
			})
			.catch((err) => {
				if (!(err instanceof AxiosError) || !err.response) return null;

				const error = err as AxiosError<Result>;

				if (!error.response?.data.isHideToastNotification) return;

				const messages = error.response?.data.errorMessages;

				if (!messages?.length) return null;

				const message = messages[0]?.value;

				if (!message) return null;

				const [firstPart, secondPart] = message.split(":");

				if (!firstPart || !secondPart) return null;

				const content = (
					<S.ConfirmationContainer>
						<S.ConfirmationTitle>
							<span>{firstPart.trim()}</span>
						</S.ConfirmationTitle>

						{secondPart.trim()}
					</S.ConfirmationContainer>
				);

				showAlert(content);
			})
			.finally(() => setIsSaving(false));
	}

	function findAndSetValue(name: Path<ITermsOfUseRegisterForm>, data: KeyValue[] | null, key: string) {
		const value = data?.find((d) => String(d.key) === key);

		if (!value) return;

		setValue(name, value);
	}

	function load(guid: string) {
		setIsLoadingTerm(true);
		fileImportApi
			.getDetailsTerm({ fileImportGuid: guid })
			.then((res) => {
				const { result } = res.data;

				if (result) {
					callFileTypeApiDomain({ key: result.idSystemApplication, value: "" });
					callCompanyApiDomain({ key: result.countryGuid, value: "" }, { key: result.idCompanyType, value: "" });
					findAndSetValue("country", countries, result.countryGuid);
					findAndSetValue("companyType", companyTypes, String(result.idCompanyType));
					findAndSetValue("systemApplication", systemApplications, String(result.idSystemApplication));
					findAndSetValue("culture", cultures, result.cultureGuid);
					const statusResult = result.active ? StatusRegisterEnum.Active : StatusRegisterEnum.Suspended;
					findAndSetValue("statusRegister", getOptionsStatus(), String(statusResult));

					const file = new File(
						[Uint8Array.from(atob(result.fileBase64), (c) => c.charCodeAt(0))],
						result.fileImportName,
						{
							type: "application/pdf",
						},
					);

					setValue("file", file);
					setHasChangedFile(false);
					setTermData(result);
				}
			})
			.finally(() => setIsLoadingTerm(false));
	}

	const isLoadingReducers =
		isLoadingCompanies ||
		isLoadingCompanyTypes ||
		isLoadingCountries ||
		isLoadingCultures ||
		isLoadingFileTypes ||
		isLoadingSystemApplications;

	return (
		<>
			<form onSubmit={handleSubmit(onSubmitForm)} noValidate>
				<S.Container>
					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-country`}
						label={getTranslateByKey("information.country")}
						control={control}
						options={countries}
						isLoading={isLoadingCountries || isLoadingTerm}
						nameCustom="country"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.country?.message ?? ""}
						hasErrorCustom={!!errors.country}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							const companyTypeSelected = getValues().companyType;
							if (companyTypeSelected) {
								callCompanyApiDomain(optionSelected, companyTypeSelected);
							}

							if (optionSelected) callCultureApiDomain(optionSelected);
							setValue("culture", null);
						}}
						valueDefaultKey={optionDefaultCountryDrop?.key}
						setValueCustom={setValue}
						disabled={optionDefaultCountryDrop !== null}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-companyType`}
						label={getTranslateByKey("information.companyType")}
						control={control}
						options={companyTypes}
						isLoading={isLoadingCompanyTypes || isLoadingTerm}
						nameCustom="companyType"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.companyType?.message ?? ""}
						setValueCustom={setValue}
						hasErrorCustom={!!errors.companyType}
						isDefaultValueUnique
						disabled={isDisabledCompanyTypeDrop}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							setValue("company", []);
							callCompanyApiDomain(getValues().country, optionSelected);
						}}
					/>

					<KogInputAutoCompleteCheckControlled
						name="company"
						control={control}
						disableVirtualization
						label={getTranslateByKey("information.company")}
						options={companies || []}
						required
						errorMessageCustom={errors.company?.message ?? ""}
						hasErrorCustom={!!errors.company}
						customInputProps={{
							id: `${ID_BASE_COMPONENTS}-select-company`,
							required: true,
						}}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						isLoading={isLoadingCompanies || isLoadingTerm}
						isDefaultValueUnique={companies === null || companies.length === 1}
						onChangeCustom={() => {}}
						{...({ disabled: isDisabledCompanyDrop() } as any)}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-system`}
						label={getTranslateByKey("information.systemApplication")}
						control={control}
						nameCustom="systemApplication"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.systemApplication?.message ?? ""}
						hasErrorCustom={!!errors.systemApplication}
						options={systemApplications}
						isLoading={isLoadingSystemApplications || isLoadingTerm}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							if (!isNullUndefined(optionSelected)) {
								setValue("fileType", null);
								callFileTypeApiDomain(optionSelected);
							}
						}}
						setValueCustom={setValue}
						disabled={isEdit}
					/>

					<KogAutoCompleteControlled
						id={`${ID_BASE_COMPONENTS}-select-fileType`}
						label={getTranslateByKey("information.term")}
						control={control}
						nameCustom="fileType"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.fileType?.message ?? ""}
						hasErrorCustom={!!errors.fileType}
						required
						options={fileTypes}
						isLoading={isLoadingFileTypes || isLoadingTerm}
						onChangeCustom={() => {}}
						setValueCustom={setValue}
						disabled={isDisabledFileTypeDrop}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-culture`}
						label={getTranslateByKey("information.userLanguage")}
						control={control}
						options={cultures}
						isLoading={isLoadingCultures || isLoadingTerm}
						nameCustom="culture"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.culture?.message ?? ""}
						setValueCustom={setValue}
						hasErrorCustom={!!errors.culture}
						isDefaultValueUnique
						onChangeCustom={() => {}}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-statusRegister`}
						label={getTranslateByKey("information.status")}
						nameCustom="statusRegister"
						errorMessageCustom={errors.statusRegister?.message ?? ""}
						hasErrorCustom={!!errors.statusRegister}
						control={control}
						setValueCustom={setValue}
						options={statusOptions}
						isLoading={isLoadingTerm}
						rulesCustom={{ required: true }}
						valueDefaultKey={defaultStatusOption?.key}
						onChangeCustom={() => {}}
					/>

					<KOGFileUploader
						files={filesForUploader}
						setFiles={(value) => {
							const newFiles = typeof value === "function" ? value(filesForUploader) : value;
							setValue("file", newFiles[0] ?? null, { shouldValidate: true });
						}}
						maxFiles={1}
						typeFiles={["pdf"]}
						isFullScreenDragDrop
						isRequired
						onCustomDelete={() => {
							setHasChangedFile(true);
						}}
						onCustomInsert={() => {
							setHasChangedFile(true);
						}}
					/>
				</S.Container>
				<S.AreaButtons>
					<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-back`} onClick={() => handleClose(false)}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button
						id={`${ID_BASE_COMPONENTS}-btn-save`}
						type="submit"
						disabled={isSaving || isLoadingTerm || isLoadingReducers}
					>
						{getTranslateByKey("information.save")}
					</Button>
				</S.AreaButtons>
			</form>

			<ModalAlert />
		</>
	);
}

export default function ModalRegisterTermsOfUse(props: Readonly<IModalRegisterTermsOfUse>) {
	const isEdit = !isNullUndefined(props.fileImportGuid);

	return (
		<KogModalSideComponent
			title={
				isEdit ? getTranslateByKey("information.editTermsOfUse") : getTranslateByKey("information.registerTermsOfUse")
			}
			open={props.isOpen}
			closeModal={props.handleClose}
		>
			{props.isOpen && <ModalRegisterTermsOfUseContent {...props} />}
		</KogModalSideComponent>
	);
}
